<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import endOfDay from 'date-fns/endOfDay';
import getUnixTime from 'date-fns/getUnixTime';
import startOfDay from 'date-fns/startOfDay';
import subDays from 'date-fns/subDays';
import format from 'date-fns/format';

import ReportHeader from './components/ReportHeader.vue';
import WootDateRangePicker from 'dashboard/components/ui/DateRangePicker.vue';
import Spinner from 'shared/components/Spinner.vue';
import LeadsReportsAPI from 'dashboard/api/leadsReports';
import InboxesAPI from 'dashboard/api/inboxes';

const { t } = useI18n();
const store = useStore();

const activeTab = ref('leads');
const isLoading = ref(false);
const isClicksLoading = ref(false);
const isCampaignLoading = ref(false);
const reportData = ref(null);
const clicksData = ref(null);
const campaignData = ref(null);
const strategies = ref([]);
const selectedStrategy = ref(null);
const selectedFunnel = ref(null);
const selectedCampaignInbox = ref(null);
const groupBy = ref('day');

const currentDateRangeSelection = ref({
  id: 0,
  name: t('REPORT.DATE_RANGE_OPTIONS.LAST_7_DAYS'),
});
const customDateRange = ref([new Date(), new Date()]);
const clicksDateRange = ref([subDays(new Date(), 29), new Date()]);

const CUSTOM_DATE_RANGE_ID = 5;

const dateRange = computed(() => [
  { id: 0, name: t('REPORT.DATE_RANGE_OPTIONS.LAST_7_DAYS') },
  { id: 1, name: t('REPORT.DATE_RANGE_OPTIONS.LAST_30_DAYS') },
  { id: 2, name: t('REPORT.DATE_RANGE_OPTIONS.LAST_3_MONTHS') },
  { id: 3, name: t('REPORT.DATE_RANGE_OPTIONS.LAST_6_MONTHS') },
  { id: 4, name: t('REPORT.DATE_RANGE_OPTIONS.LAST_YEAR') },
  { id: 5, name: t('REPORT.DATE_RANGE_OPTIONS.CUSTOM_DATE_RANGE') },
]);

const groupByOptions = computed(() => [
  { id: 'day', name: t('REPORT.GROUPING_OPTIONS.DAY') },
  { id: 'week', name: t('REPORT.GROUPING_OPTIONS.WEEK') },
  { id: 'month', name: t('REPORT.GROUPING_OPTIONS.MONTH') },
]);

const isDateRangeSelected = computed(
  () => currentDateRangeSelection.value.id === CUSTOM_DATE_RANGE_ID
);

const funnels = computed(() => store.getters['funnels/getFunnels'] || []);

const fromDate = computed(() => {
  if (isDateRangeSelected.value) {
    return getUnixTime(startOfDay(customDateRange.value[0]));
  }
  const dateRangeMap = { 0: 6, 1: 29, 2: 89, 3: 179, 4: 364 };
  const diff = dateRangeMap[currentDateRangeSelection.value.id];
  return getUnixTime(startOfDay(subDays(new Date(), diff)));
});

const toDate = computed(() => {
  if (isDateRangeSelected.value) {
    return getUnixTime(endOfDay(customDateRange.value[1]));
  }
  return getUnixTime(endOfDay(new Date()));
});

const chartData = computed(() => {
  if (!reportData.value?.by_date) return [];

  const entries = Object.entries(reportData.value.by_date);
  return entries
    .map(([date, count]) => ({
      date: formatDate(date),
      rawDate: date,
      count,
    }))
    .sort((a, b) => new Date(a.rawDate) - new Date(b.rawDate));
});

const strategyBreakdown = computed(() => {
  if (!reportData.value?.by_strategy) return [];
  return reportData.value.by_strategy;
});

const summary = computed(() => reportData.value?.summary || {});
const clicksSummary = computed(() => clicksData.value?.summary || {});
const clicksByIp = computed(() => clicksData.value?.by_ip || []);
const clicksByRegion = computed(() => clicksData.value?.by_region || []);
const clicksByStrategy = computed(() => clicksData.value?.by_strategy || []);

const maxCount = computed(() => {
  if (!chartData.value.length) return 0;
  return Math.max(...chartData.value.map(d => d.count));
});

const inboxes = computed(() => store.getters['inboxes/getInboxes'] || []);
const allCampaignLeads = computed(() => campaignData.value?.leads || []);
const campaignSummary = computed(() => campaignData.value?.campaigns || []);
const campaignTotals = computed(() => campaignData.value?.summary || {});
const agentSummary = computed(() => campaignData.value?.agents || []);

// Agent filter
const selectedAgent = ref(null);
const agentOptions = computed(() => {
  const agents = agentSummary.value.map(a => a.agent).filter(Boolean);
  return [
    { id: null, name: 'Todos os agentes' },
    ...agents.map(a => ({ id: a, name: a })),
  ];
});

// LP Code filter
const selectedLpCode = ref(null);

// Search
const leadSearchQuery = ref('');

// Filtered leads (by agent + search)
const campaignLeads = computed(() => {
  let leads = allCampaignLeads.value;
  if (selectedAgent.value) {
    leads = leads.filter(l => (l.agent || '-') === selectedAgent.value);
  }
  if (selectedLpCode.value) {
    leads = leads.filter(l => l.campaign_code === selectedLpCode.value);
  }
  const q = leadSearchQuery.value.trim().toLowerCase();
  if (q) {
    leads = leads.filter(l =>
      (l.name || '').toLowerCase().includes(q) ||
      (l.phone || '').toLowerCase().includes(q) ||
      (l.campaign_code || '').toLowerCase().includes(q) ||
      (l.campaign || '').toLowerCase().includes(q) ||
      (l.inbox || '').toLowerCase().includes(q) ||
      (l.agent || '').toLowerCase().includes(q) ||
      (l.procedure || '').toLowerCase().includes(q)
    );
  }
  return leads;
});

// Pagination
const currentPage = ref(1);
const pageSize = 25;
const totalPages = computed(() =>
  Math.max(1, Math.ceil(campaignLeads.value.length / pageSize))
);
const paginatedLeads = computed(() => {
  const start = (currentPage.value - 1) * pageSize;
  return campaignLeads.value.slice(start, start + pageSize);
});

function onAgentSelect(event) {
  const val = event.target.value;
  selectedAgent.value = val || null;
  currentPage.value = 1;
}

function onLpCodeSelect(event) {
  const val = event.target.value;
  selectedLpCode.value = val || null;
  currentPage.value = 1;
}

function toggleLpCodeFilter(code) {
  selectedLpCode.value = selectedLpCode.value === code ? null : code;
  currentPage.value = 1;
}

function goToPage(page) {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
  }
}

const campaignDateRange = ref([subDays(new Date(), 29), new Date()]);
const isSyncing = ref(false);
const syncResult = ref(null);
const webhookUrl = ref('');
const isSavingWebhook = ref(false);
const webhookSaved = ref(false);
const heatmapData = ref(null);
const isHeatmapLoading = ref(false);
const heatmapTooltip = ref({ visible: false, x: 0, y: 0, text: '' });
const leadsWithCode = computed(() =>
  campaignLeads.value.filter(l => l.campaign_code)
);

// LP Code options with counts (derived from all leads, not filtered by LP code itself)
const lpCodeOptions = computed(() => {
  const counts = {};
  allCampaignLeads.value.forEach(l => {
    if (l.campaign_code) {
      counts[l.campaign_code] = (counts[l.campaign_code] || 0) + 1;
    }
  });
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .map(([code, count]) => ({ id: code, name: `${code} (${count})` }));
});

// LP Code summary breakdown (with + without code)
const lpCodeSummary = computed(() => {
  const counts = {};
  let withoutCode = 0;
  allCampaignLeads.value.forEach(l => {
    if (l.campaign_code) {
      counts[l.campaign_code] = (counts[l.campaign_code] || 0) + 1;
    } else {
      withoutCode += 1;
    }
  });
  const rows = Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .map(([code, count]) => ({ code, count }));
  return {
    rows,
    withoutCode,
    withCode: allCampaignLeads.value.length - withoutCode,
  };
});

// Traffic origin summary with status_cliente from webhook
const trafficOriginSummary = computed(() => {
  const origins = {
    pago: {
      label: 'Pago (Anúncio / Story)',
      total: 0, comLp: 0, semLp: 0,
      sem_venda: 0, em_tratamento: 0, finalizado: 0, sem_status: 0,
    },
    organico: {
      label: 'Orgânico',
      total: 0, comLp: 0, semLp: 0,
      sem_venda: 0, em_tratamento: 0, finalizado: 0, sem_status: 0,
    },
    direto: {
      label: 'Direto',
      total: 0, comLp: 0, semLp: 0,
      sem_venda: 0, em_tratamento: 0, finalizado: 0, sem_status: 0,
    },
  };

  allCampaignLeads.value.forEach(l => {
    const camp = (l.campaign || '').toLowerCase();
    const hasLp = !!l.campaign_code;
    const status = l.status_cliente;

    let bucket;
    if (camp.includes('orgânico') || camp.includes('organico')) {
      bucket = origins.organico;
    } else if (
      camp.includes('anúncio') ||
      camp.includes('anuncio') ||
      camp.includes('resposta ao story')
    ) {
      bucket = origins.pago;
    } else {
      bucket = origins.direto;
    }

    bucket.total += 1;
    if (hasLp) bucket.comLp += 1;
    else bucket.semLp += 1;

    if (status === 'sem_venda') bucket.sem_venda += 1;
    else if (status === 'em_tratamento') bucket.em_tratamento += 1;
    else if (status === 'finalizado') bucket.finalizado += 1;
    else bucket.sem_status += 1;
  });

  return [origins.pago, origins.organico, origins.direto];
});

// Status totals
const statusTotals = computed(() => {
  const totals = {
    sem_venda: 0, em_tratamento: 0, finalizado: 0, sem_status: 0,
  };
  allCampaignLeads.value.forEach(l => {
    const status = l.status_cliente;
    if (status === 'sem_venda') totals.sem_venda += 1;
    else if (status === 'em_tratamento') totals.em_tratamento += 1;
    else if (status === 'finalizado') totals.finalizado += 1;
    else totals.sem_status += 1;
  });
  return totals;
});

// Lead Webhook Inboxes
const leadWebhookInboxes = ref([]);
const isSavingLeadInboxes = ref(false);
const leadInboxesSaved = ref(false);

const autoSyncEnabled = ref(false);
const autoSyncInterval = ref(60);
const autoSyncLastRun = ref(null);
const isSavingAutoSync = ref(false);
const autoSyncSaved = ref(false);

// Lead Managers
const managers = ref([]);
const managerSettings = ref({
  inbox_id: null,
  notify_realtime: false,
  digest_enabled: false,
  digest_days: [1, 2, 3, 4, 5],
  digest_hours: ['09:00'],
});
const isManagersLoading = ref(false);
const isSavingManager = ref(false);
const isSavingManagerSettings = ref(false);
const managerSettingsSaved = ref(false);
const newManagerName = ref('');
const newManagerPhone = ref('');
const selectedManagerInbox = ref(null);
const managerInboxOptions = ref([]);
const isSendingDigest = ref(false);
const digestResult = ref(null);

const DIAS_SEMANA_LABELS = [
  { id: 0, name: 'Dom' },
  { id: 1, name: 'Seg' },
  { id: 2, name: 'Ter' },
  { id: 3, name: 'Qua' },
  { id: 4, name: 'Qui' },
  { id: 5, name: 'Sex' },
  { id: 6, name: 'Sáb' },
];

const HOUR_OPTIONS = [
  '06:00',
  '07:00',
  '08:00',
  '09:00',
  '10:00',
  '11:00',
  '12:00',
  '13:00',
  '14:00',
  '15:00',
  '16:00',
  '17:00',
  '18:00',
  '19:00',
  '20:00',
  '21:00',
];

const autoSyncIntervalOptions = [
  { id: 15, name: 'A cada 15 minutos' },
  { id: 30, name: 'A cada 30 minutos' },
  { id: 60, name: 'A cada 1 hora' },
  { id: 120, name: 'A cada 2 horas' },
  { id: 360, name: 'A cada 6 horas' },
  { id: 720, name: 'A cada 12 horas' },
  { id: 1440, name: 'A cada 24 horas' },
];

const selectedAutoSyncInterval = computed({
  get: () =>
    autoSyncIntervalOptions.find(o => o.id === autoSyncInterval.value) ||
    autoSyncIntervalOptions[2],
  set: val => {
    autoSyncInterval.value = val.id;
  },
});

function formatLastRun(isoStr) {
  if (!isoStr) return 'Nunca';
  try {
    return format(new Date(isoStr), 'dd/MM/yyyy HH:mm');
  } catch {
    return isoStr;
  }
}

function formatDate(dateStr) {
  try {
    const date = new Date(dateStr);
    return format(date, 'dd/MM/yyyy');
  } catch {
    return dateStr;
  }
}

function getBarHeight(count) {
  if (!maxCount.value) return 0;
  return (count / maxCount.value) * 100;
}

async function fetchStrategies() {
  try {
    const response = await LeadsReportsAPI.getStrategies();
    strategies.value = [
      { id: null, name: t('LEADS_REPORTS.ALL_STRATEGIES') },
      ...response.data,
    ];
  } catch (error) {
    console.error('Error fetching strategies:', error);
  }
}

async function fetchFunnels() {
  await store.dispatch('funnels/fetchFunnels');
}

async function fetchReport() {
  isLoading.value = true;
  try {
    const response = await LeadsReportsAPI.getLeadsReport({
      from: fromDate.value,
      to: toDate.value,
      strategyId: selectedStrategy.value?.id,
      funnelId: selectedFunnel.value?.id,
      groupBy: groupBy.value,
    });
    reportData.value = response.data;
  } catch (error) {
    console.error('Error fetching leads report:', error);
  } finally {
    isLoading.value = false;
  }
}

async function fetchClicksReport() {
  isClicksLoading.value = true;
  try {
    const from = getUnixTime(startOfDay(clicksDateRange.value[0]));
    const to = getUnixTime(endOfDay(clicksDateRange.value[1]));
    const response = await LeadsReportsAPI.getClicksReport({
      from,
      to,
      strategyId: selectedStrategy.value?.id,
    });
    clicksData.value = response.data;
  } catch (error) {
    console.error('Error fetching clicks report:', error);
  } finally {
    isClicksLoading.value = false;
  }
}

const DIAS_SEMANA = [
  'Domingo',
  'Segunda',
  'Terça',
  'Quarta',
  'Quinta',
  'Sexta',
  'Sábado',
];

function heatmapMax() {
  if (!heatmapData.value) return 1;
  return Math.max(1, ...heatmapData.value.flat());
}

function heatmapCellColor(value) {
  if (!value) return 'bg-n-slate-2 dark:bg-n-slate-1/30';
  const max = heatmapMax();
  const ratio = value / max;
  if (ratio <= 0.15) return 'bg-n-blue-3';
  if (ratio <= 0.3) return 'bg-n-blue-5';
  if (ratio <= 0.5) return 'bg-n-blue-7';
  if (ratio <= 0.7) return 'bg-n-blue-9';
  if (ratio <= 0.85) return 'bg-n-blue-10';
  return 'bg-n-blue-11';
}

function showHeatmapTooltip(event, day, hour, value) {
  const rect = event.target.getBoundingClientRect();
  heatmapTooltip.value = {
    visible: true,
    x: rect.left + rect.width / 2,
    y: rect.top,
    text: `${DIAS_SEMANA[day]} ${hour}h - ${hour + 1}h: ${value} leads`,
  };
}

function hideHeatmapTooltip() {
  heatmapTooltip.value.visible = false;
}

async function fetchLeadsHeatmap() {
  isHeatmapLoading.value = true;
  try {
    const from = getUnixTime(startOfDay(campaignDateRange.value[0]));
    const to = getUnixTime(endOfDay(campaignDateRange.value[1]));
    const response = await LeadsReportsAPI.getLeadsHeatmap({
      from,
      to,
      inboxId: selectedCampaignInbox.value?.id,
    });
    heatmapData.value = response.data.heatmap;
  } catch (error) {
    console.error('Error fetching leads heatmap:', error);
  } finally {
    isHeatmapLoading.value = false;
  }
}

async function fetchCampaignLeads() {
  isCampaignLoading.value = true;
  try {
    const from = getUnixTime(startOfDay(campaignDateRange.value[0]));
    const to = getUnixTime(endOfDay(campaignDateRange.value[1]));
    const response = await LeadsReportsAPI.getCampaignLeads({
      from,
      to,
      inboxId: selectedCampaignInbox.value?.id,
    });
    campaignData.value = response.data;
    currentPage.value = 1;
  } catch (error) {
    console.error('Error fetching campaign leads:', error);
  } finally {
    isCampaignLoading.value = false;
  }
}

function onCampaignDateChange(value) {
  campaignDateRange.value = value;
  fetchCampaignLeads();
  fetchLeadsHeatmap();
}

function onCampaignInboxChange() {
  fetchCampaignLeads();
  fetchLeadsHeatmap();
}

async function loadAutoSyncSettings() {
  try {
    const response = await LeadsReportsAPI.getAutoSyncSettings();
    autoSyncEnabled.value = response.data.enabled;
    autoSyncInterval.value = response.data.interval || 60;
    autoSyncLastRun.value = response.data.last_run;
  } catch (error) {
    console.error('Error loading auto-sync settings:', error);
  }
}

async function saveAutoSyncSettings() {
  isSavingAutoSync.value = true;
  autoSyncSaved.value = false;
  try {
    const response = await LeadsReportsAPI.saveAutoSyncSettings({
      enabled: autoSyncEnabled.value,
      interval: autoSyncInterval.value,
    });
    autoSyncLastRun.value = response.data.last_run;
    autoSyncSaved.value = true;
    setTimeout(() => {
      autoSyncSaved.value = false;
    }, 3000);
  } catch (error) {
    console.error('Error saving auto-sync settings:', error);
  } finally {
    isSavingAutoSync.value = false;
  }
}

async function loadWebhookUrl() {
  try {
    const response = await LeadsReportsAPI.getWebhookUrl();
    webhookUrl.value = response.data.webhook_url || '';
  } catch (error) {
    console.error('Error loading webhook URL:', error);
  }
}

async function saveWebhookUrl() {
  isSavingWebhook.value = true;
  webhookSaved.value = false;
  try {
    await LeadsReportsAPI.saveWebhookUrl(webhookUrl.value.trim());
    webhookSaved.value = true;
    setTimeout(() => {
      webhookSaved.value = false;
    }, 3000);
  } catch (error) {
    console.error('Error saving webhook URL:', error);
  } finally {
    isSavingWebhook.value = false;
  }
}

async function syncCampaignLeads() {
  if (!webhookUrl.value.trim()) return;
  isSyncing.value = true;
  syncResult.value = { queued: true, message: 'Sincronização iniciada...' };
  try {
    const from = getUnixTime(startOfDay(campaignDateRange.value[0]));
    const to = getUnixTime(endOfDay(campaignDateRange.value[1]));
    await LeadsReportsAPI.syncCampaignLeads({
      from,
      to,
      inboxId: selectedCampaignInbox.value?.id,
      webhookUrl: webhookUrl.value.trim(),
    });
    // Poll for result from background job
    pollSyncStatus();
  } catch (error) {
    console.error('Error syncing campaign leads:', error);
    const serverMsg = error.response?.data?.message;
    syncResult.value = { error: true, message: serverMsg || error.message };
    isSyncing.value = false;
  }
}

function pollSyncStatus() {
  const interval = setInterval(async () => {
    try {
      const response = await LeadsReportsAPI.syncCampaignLeadsStatus();
      const result = response.data?.result;
      if (result && result.finished_at) {
        clearInterval(interval);
        syncResult.value = result;
        isSyncing.value = false;
        // Reload leads to update sync status column
        fetchCampaignLeads();
      }
    } catch {
      // keep polling
    }
  }, 3000);
  // Stop polling after 10 minutes
  setTimeout(() => {
    clearInterval(interval);
    if (isSyncing.value) {
      syncResult.value = { error: true, message: 'Timeout aguardando resultado. Verifique novamente.' };
      isSyncing.value = false;
    }
  }, 600000);
}

function onClicksDateChange(value) {
  clicksDateRange.value = value;
  fetchClicksReport();
}

function fetchActiveTab() {
  if (activeTab.value === 'leads') {
    fetchReport();
  } else if (activeTab.value === 'clicks') {
    fetchClicksReport();
  } else if (activeTab.value === 'campaigns') {
    fetchCampaignLeads();
    fetchLeadsHeatmap();
  }
}

async function onTabChange(tab) {
  activeTab.value = tab;
  if (tab === 'managers') {
    await loadManagerInboxes();
    loadManagers();
    loadManagerSettings();
  } else {
    fetchActiveTab();
  }
}

function onDateRangeChange(selectedRange) {
  currentDateRangeSelection.value = selectedRange;
  fetchActiveTab();
}

function onCustomDateChange(value) {
  customDateRange.value = value;
  fetchActiveTab();
}

function onStrategyChange() {
  fetchActiveTab();
}

function onStrategySelect(event) {
  const val = event.target.value;
  selectedStrategy.value = val
    ? strategies.value.find(s => s.id === Number(val)) || null
    : null;
  onStrategyChange();
}

function onFunnelChange() {
  fetchReport();
}

function onFunnelSelect(event) {
  const val = event.target.value;
  selectedFunnel.value = val
    ? funnels.value.find(f => f.id === Number(val)) || null
    : null;
  onFunnelChange();
}

function onGroupByChange() {
  fetchReport();
}

function onGroupBySelect(event) {
  const sel = groupByOptions.value.find(g => g.id === event.target.value);
  if (sel) {
    groupBy.value = sel;
    onGroupByChange();
  }
}

function onDateRangeSelect(event) {
  const sel = dateRange.value.find(d => d.id === Number(event.target.value));
  if (sel) {
    currentDateRangeSelection.value = sel;
    onDateRangeChange(sel);
  }
}

function onManagerInboxSelect(event) {
  const val = Number(event.target.value);
  selectedManagerInbox.value =
    managerInboxOptions.value.find(i => i.id === val) || null;
}

function onCampaignInboxSelect(event) {
  const val = event.target.value;
  selectedCampaignInbox.value = val
    ? inboxes.value.find(i => i.id === Number(val)) || null
    : null;
  onCampaignInboxChange();
}

function onAutoSyncIntervalSelect(event) {
  const sel = autoSyncIntervalOptions.find(
    o => o.id === Number(event.target.value)
  );
  if (sel) selectedAutoSyncInterval.value = sel;
}

// ==================== Lead Managers ====================

async function loadManagerInboxes() {
  try {
    const response = await InboxesAPI.get();
    const payload = response.data?.payload || [];
    managerInboxOptions.value = payload;
  } catch (error) {
    console.error('Error loading inboxes for managers:', error);
    // Fallback to store
    managerInboxOptions.value = inboxes.value;
  }
}

async function loadManagers() {
  isManagersLoading.value = true;
  try {
    const response = await LeadsReportsAPI.getLeadManagers();
    managers.value = response.data.managers || [];
  } catch (error) {
    console.error('Error loading managers:', error);
  } finally {
    isManagersLoading.value = false;
  }
}

async function loadManagerSettings() {
  try {
    const response = await LeadsReportsAPI.getLeadManagerSettings();
    managerSettings.value = response.data;
    if (managerSettings.value.inbox_id) {
      selectedManagerInbox.value =
        managerInboxOptions.value.find(
          i => i.id === managerSettings.value.inbox_id
        ) || null;
    }
  } catch (error) {
    console.error('Error loading manager settings:', error);
  }
}

async function addManager() {
  if (!newManagerName.value.trim() || !newManagerPhone.value.trim()) return;
  isSavingManager.value = true;
  try {
    const response = await LeadsReportsAPI.saveLeadManager({
      name: newManagerName.value.trim(),
      phone: newManagerPhone.value.trim(),
    });
    managers.value = response.data.managers || [];
    newManagerName.value = '';
    newManagerPhone.value = '';
  } catch (error) {
    console.error('Error adding manager:', error);
  } finally {
    isSavingManager.value = false;
  }
}

async function toggleManager(manager) {
  try {
    const response = await LeadsReportsAPI.updateLeadManager({
      managerId: manager.id,
      active: !manager.active,
    });
    managers.value = response.data.managers || [];
  } catch (error) {
    console.error('Error toggling manager:', error);
  }
}

async function removeManager(manager) {
  try {
    const response = await LeadsReportsAPI.deleteLeadManager(manager.id);
    managers.value = response.data.managers || [];
  } catch (error) {
    console.error('Error removing manager:', error);
  }
}

async function saveManagerSettings() {
  isSavingManagerSettings.value = true;
  managerSettingsSaved.value = false;
  try {
    await LeadsReportsAPI.saveLeadManagerSettings({
      inbox_id: selectedManagerInbox.value?.id,
      notify_realtime: managerSettings.value.notify_realtime,
      digest_enabled: managerSettings.value.digest_enabled,
      digest_days: managerSettings.value.digest_days,
      digest_hours: managerSettings.value.digest_hours,
    });
    managerSettingsSaved.value = true;
    setTimeout(() => {
      managerSettingsSaved.value = false;
    }, 3000);
  } catch (error) {
    console.error('Error saving manager settings:', error);
  } finally {
    isSavingManagerSettings.value = false;
  }
}

function toggleDigestDay(dayId) {
  const days = managerSettings.value.digest_days || [];
  const index = days.indexOf(dayId);
  if (index >= 0) {
    days.splice(index, 1);
  } else {
    days.push(dayId);
    days.sort();
  }
  managerSettings.value.digest_days = [...days];
}

function toggleDigestHour(hour) {
  const hours = managerSettings.value.digest_hours || [];
  const index = hours.indexOf(hour);
  if (index >= 0) {
    hours.splice(index, 1);
  } else {
    hours.push(hour);
    hours.sort();
  }
  managerSettings.value.digest_hours = [...hours];
}

async function sendDigestNow() {
  isSendingDigest.value = true;
  digestResult.value = null;
  try {
    const since = getUnixTime(subDays(new Date(), 1));
    const response = await LeadsReportsAPI.sendLeadManagerDigest({ since });
    digestResult.value = response.data;
  } catch (error) {
    console.error('Error sending digest:', error);
    digestResult.value = { error: true };
  } finally {
    isSendingDigest.value = false;
  }
}

function isInboxSelectedForLeads(inboxId) {
  return leadWebhookInboxes.value.some(i => i.id === inboxId);
}

function toggleLeadInbox(inbox) {
  const index = leadWebhookInboxes.value.findIndex(i => i.id === inbox.id);
  if (index >= 0) {
    leadWebhookInboxes.value.splice(index, 1);
  } else {
    leadWebhookInboxes.value.push({ id: inbox.id, name: inbox.name });
  }
}

async function loadLeadWebhookInboxes() {
  try {
    const response = await LeadsReportsAPI.getLeadWebhookInboxes();
    leadWebhookInboxes.value = response.data.inboxes || [];
  } catch (error) {
    console.error('Error loading lead webhook inboxes:', error);
  }
}

async function saveLeadWebhookInboxes() {
  isSavingLeadInboxes.value = true;
  leadInboxesSaved.value = false;
  try {
    const inboxIds = leadWebhookInboxes.value.map(i => i.id);
    const response = await LeadsReportsAPI.saveLeadWebhookInboxes(inboxIds);
    leadWebhookInboxes.value = response.data.inboxes || [];
    leadInboxesSaved.value = true;
    setTimeout(() => {
      leadInboxesSaved.value = false;
    }, 3000);
  } catch (error) {
    console.error('Error saving lead webhook inboxes:', error);
  } finally {
    isSavingLeadInboxes.value = false;
  }
}

onMounted(async () => {
  await Promise.all([
    fetchStrategies(),
    fetchFunnels(),
    store.dispatch('inboxes/get'),
    loadWebhookUrl(),
    loadAutoSyncSettings(),
    loadLeadWebhookInboxes(),
  ]);
  fetchReport();
});

watch([fromDate, toDate], () => {
  // Date change handled in specific methods
});
</script>

<template>
  <div>
    <ReportHeader :header-title="$t('LEADS_REPORTS.HEADER')" />

    <!-- Tabs -->
    <div class="flex gap-2 mb-6 border-b border-n-weak">
      <button
        class="px-4 py-2 text-sm font-medium transition-colors"
        :class="
          activeTab === 'leads'
            ? 'text-n-blue-11 border-b-2 border-n-blue-9'
            : 'text-n-slate-11 hover:text-n-slate-12'
        "
        @click="onTabChange('leads')"
      >
        {{ $t('LEADS_REPORTS.TAB_LEADS') }}
      </button>
      <button
        class="px-4 py-2 text-sm font-medium transition-colors"
        :class="
          activeTab === 'clicks'
            ? 'text-n-blue-11 border-b-2 border-n-blue-9'
            : 'text-n-slate-11 hover:text-n-slate-12'
        "
        @click="onTabChange('clicks')"
      >
        {{ $t('LEADS_REPORTS.TAB_CLICKS') }}
      </button>
      <button
        class="px-4 py-2 text-sm font-medium transition-colors"
        :class="
          activeTab === 'campaigns'
            ? 'text-n-blue-11 border-b-2 border-n-blue-9'
            : 'text-n-slate-11 hover:text-n-slate-12'
        "
        @click="onTabChange('campaigns')"
      >
        Campanhas
      </button>
      <button
        class="px-4 py-2 text-sm font-medium transition-colors"
        :class="
          activeTab === 'managers'
            ? 'text-n-blue-11 border-b-2 border-n-blue-9'
            : 'text-n-slate-11 hover:text-n-slate-12'
        "
        @click="onTabChange('managers')"
      >
        Gestores
      </button>
    </div>

    <!-- Filters -->
    <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
      <!-- Strategy Filter -->
      <div class="multiselect-wrap--small">
        <p class="mb-2 text-xs font-medium">
          {{ $t('LEADS_REPORTS.FILTER_STRATEGY') }}
        </p>
        <select
          :value="selectedStrategy?.id ?? ''"
          class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12"
          @change="onStrategySelect"
        >
          <option value="">
            {{ $t('LEADS_REPORTS.SELECT_STRATEGY') }}
          </option>
          <option
            v-for="s in strategies"
            :key="s.id"
            :value="s.id"
          >
            {{ s.name }}
          </option>
        </select>
      </div>

      <!-- Funnel Filter (only in leads tab) -->
      <div v-if="activeTab === 'leads'" class="multiselect-wrap--small">
        <p class="mb-2 text-xs font-medium">
          {{ $t('LEADS_REPORTS.FILTER_FUNNEL') }}
        </p>
        <select
          :value="selectedFunnel?.id ?? ''"
          class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12"
          @change="onFunnelSelect"
        >
          <option value="">
            {{ $t('LEADS_REPORTS.SELECT_FUNNEL') }}
          </option>
          <option
            v-for="f in funnels"
            :key="f.id"
            :value="f.id"
          >
            {{ f.title }}
          </option>
        </select>
      </div>

      <!-- Date Range Filter (leads tab: dropdown, clicks tab: date picker) -->
      <template v-if="activeTab === 'leads'">
        <div class="multiselect-wrap--small">
          <p class="mb-2 text-xs font-medium">
            {{ $t('REPORT.DURATION_FILTER_LABEL') }}
          </p>
          <select
            :value="currentDateRangeSelection?.id ?? ''"
            class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12"
            @change="onDateRangeSelect"
          >
            <option
              v-for="d in dateRange"
              :key="d.id"
              :value="d.id"
            >
              {{ d.name }}
            </option>
          </select>
        </div>

        <!-- Group By Filter -->
        <div class="multiselect-wrap--small">
          <p class="mb-2 text-xs font-medium">
            {{ $t('REPORT.GROUP_BY_FILTER_DROPDOWN_LABEL') }}
          </p>
          <select
            :value="groupBy?.id ?? ''"
            class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12"
            @change="onGroupBySelect"
          >
            <option
              v-for="g in groupByOptions"
              :key="g.id"
              :value="g.id"
            >
              {{ g.name || $t('REPORT.GROUPING_OPTIONS.DAY') }}
            </option>
          </select>
        </div>
      </template>

      <!-- Date Range for Clicks tab: always show date picker -->
      <div v-if="activeTab === 'clicks'" class="md:col-span-2">
        <p class="mb-2 text-xs font-medium">
          {{ $t('LEADS_REPORTS.CLICKS_DATE_FILTER') }}
        </p>
        <WootDateRangePicker
          show-range
          :value="clicksDateRange"
          :confirm-text="$t('REPORT.CUSTOM_DATE_RANGE.CONFIRM')"
          :placeholder="$t('LEADS_REPORTS.CLICKS_DATE_FILTER')"
          @change="onClicksDateChange"
        />
      </div>
    </div>

    <!-- Custom Date Range (leads tab only) -->
    <div v-if="activeTab === 'leads' && isDateRangeSelected" class="mb-6">
      <p class="mb-2 text-xs font-medium">
        {{ $t('REPORT.CUSTOM_DATE_RANGE.PLACEHOLDER') }}
      </p>
      <WootDateRangePicker
        show-range
        :value="customDateRange"
        :confirm-text="$t('REPORT.CUSTOM_DATE_RANGE.CONFIRM')"
        :placeholder="$t('REPORT.CUSTOM_DATE_RANGE.PLACEHOLDER')"
        @change="onCustomDateChange"
      />
    </div>

    <!-- ==================== LEADS TAB ==================== -->
    <template v-if="activeTab === 'leads'">
      <div v-if="isLoading" class="flex items-center justify-center py-12">
        <Spinner />
      </div>

      <div v-else-if="reportData">
        <!-- Summary Cards -->
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
            <p class="text-sm text-n-slate-11 mb-1">
              {{ $t('LEADS_REPORTS.TOTAL_LEADS') }}
            </p>
            <p class="text-2xl font-semibold text-n-slate-12">
              {{ summary.total_leads || 0 }}
            </p>
          </div>
          <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
            <p class="text-sm text-n-slate-11 mb-1">
              {{ $t('LEADS_REPORTS.WITH_STRATEGY') }}
            </p>
            <p class="text-2xl font-semibold text-n-blue-11">
              {{ summary.leads_with_strategy || 0 }}
            </p>
          </div>
          <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
            <p class="text-sm text-n-slate-11 mb-1">
              {{ $t('LEADS_REPORTS.WITHOUT_STRATEGY') }}
            </p>
            <p class="text-2xl font-semibold text-n-amber-11">
              {{ summary.leads_without_strategy || 0 }}
            </p>
          </div>
          <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
            <p class="text-sm text-n-slate-11 mb-1">
              {{ $t('LEADS_REPORTS.STORY_REPLIES') }}
            </p>
            <p class="text-2xl font-semibold text-n-violet-11">
              {{ summary.story_replies || 0 }}
            </p>
          </div>
        </div>

        <!-- Chart -->
        <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak mb-6">
          <h3 class="text-lg font-semibold mb-4 text-n-slate-12">
            {{ $t('LEADS_REPORTS.CHART_TITLE') }}
          </h3>
          <div v-if="chartData.length" class="h-64">
            <div class="flex items-end justify-between h-full gap-1">
              <div
                v-for="item in chartData"
                :key="item.rawDate"
                class="flex flex-col items-center flex-1"
              >
                <span class="text-xs text-n-slate-11 mb-1">{{
                  item.count
                }}</span>
                <div
                  class="w-full bg-n-blue-9 rounded-t transition-all duration-300"
                  :style="{
                    height: `${getBarHeight(item.count)}%`,
                    minHeight: item.count > 0 ? '4px' : '0',
                  }"
                />
                <span
                  class="text-xs text-n-slate-11 mt-2 rotate-45 origin-left whitespace-nowrap"
                >
                  {{ item.date }}
                </span>
              </div>
            </div>
          </div>
          <div
            v-else
            class="flex items-center justify-center h-64 text-n-slate-11"
          >
            {{ $t('LEADS_REPORTS.NO_DATA') }}
          </div>
        </div>

        <!-- Strategy Breakdown Table -->
        <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
          <h3 class="text-lg font-semibold mb-4 text-n-slate-12">
            {{ $t('LEADS_REPORTS.BY_STRATEGY') }}
          </h3>
          <div v-if="strategyBreakdown.length" class="overflow-x-auto">
            <table class="w-full">
              <thead>
                <tr class="border-b border-n-weak">
                  <th
                    class="text-left py-3 px-4 text-sm font-medium text-n-slate-11"
                  >
                    {{ $t('LEADS_REPORTS.TABLE.STRATEGY') }}
                  </th>
                  <th
                    class="text-left py-3 px-4 text-sm font-medium text-n-slate-11"
                  >
                    {{ $t('LEADS_REPORTS.TABLE.CAMPAIGN_ID') }}
                  </th>
                  <th
                    class="text-right py-3 px-4 text-sm font-medium text-n-slate-11"
                  >
                    {{ $t('LEADS_REPORTS.TABLE.LEADS_COUNT') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="strategy in strategyBreakdown"
                  :key="strategy.strategy_id"
                  class="border-b border-n-weak hover:bg-n-alpha-2"
                >
                  <td class="py-3 px-4 text-sm text-n-slate-12">
                    {{ strategy.strategy_name }}
                  </td>
                  <td class="py-3 px-4 text-sm text-n-slate-11">
                    {{ strategy.id_campanha || '-' }}
                  </td>
                  <td
                    class="py-3 px-4 text-sm text-n-slate-12 text-right font-medium"
                  >
                    {{ strategy.leads_count }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="text-center py-8 text-n-slate-11">
            {{ $t('LEADS_REPORTS.NO_STRATEGY_DATA') }}
          </div>
        </div>
      </div>

      <div v-else class="text-center py-12 text-n-slate-11">
        {{ $t('LEADS_REPORTS.NO_DATA') }}
      </div>
    </template>

    <!-- ==================== CLICKS TAB ==================== -->
    <template v-if="activeTab === 'clicks'">
      <div
        v-if="isClicksLoading"
        class="flex items-center justify-center py-12"
      >
        <Spinner />
      </div>

      <div v-else-if="clicksData">
        <!-- Summary Cards -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
            <p class="text-sm text-n-slate-11 mb-1">
              {{ $t('LEADS_REPORTS.TOTAL_CLICKS') }}
            </p>
            <p class="text-2xl font-semibold text-n-slate-12">
              {{ clicksSummary.total_clicks || 0 }}
            </p>
          </div>
          <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
            <p class="text-sm text-n-slate-11 mb-1">
              {{ $t('LEADS_REPORTS.UNIQUE_IPS') }}
            </p>
            <p class="text-2xl font-semibold text-n-blue-11">
              {{ clicksSummary.unique_ips || 0 }}
            </p>
          </div>
        </div>

        <!-- Clicks by IP Table -->
        <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak mb-6">
          <h3 class="text-lg font-semibold mb-4 text-n-slate-12">
            {{ $t('LEADS_REPORTS.CLICKS_BY_IP') }}
          </h3>
          <div v-if="clicksByIp.length" class="overflow-x-auto">
            <table class="w-full">
              <thead>
                <tr class="border-b border-n-weak">
                  <th
                    class="text-left py-3 px-4 text-sm font-medium text-n-slate-11"
                  >
                    {{ $t('LEADS_REPORTS.CLICKS_TABLE.IP_ADDRESS') }}
                  </th>
                  <th
                    class="text-right py-3 px-4 text-sm font-medium text-n-slate-11"
                  >
                    {{ $t('LEADS_REPORTS.CLICKS_TABLE.CLICKS') }}
                  </th>
                  <th
                    class="text-left py-3 px-4 text-sm font-medium text-n-slate-11"
                  >
                    {{ $t('LEADS_REPORTS.CLICKS_TABLE.CITY') }}
                  </th>
                  <th
                    class="text-left py-3 px-4 text-sm font-medium text-n-slate-11"
                  >
                    {{ $t('LEADS_REPORTS.CLICKS_TABLE.REGION') }}
                  </th>
                  <th
                    class="text-left py-3 px-4 text-sm font-medium text-n-slate-11"
                  >
                    {{ $t('LEADS_REPORTS.CLICKS_TABLE.COUNTRY') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="item in clicksByIp"
                  :key="item.ip_address"
                  class="border-b border-n-weak hover:bg-n-alpha-2"
                >
                  <td class="py-3 px-4 text-sm text-n-slate-12 font-mono">
                    {{ item.ip_address }}
                  </td>
                  <td
                    class="py-3 px-4 text-sm text-n-slate-12 text-right font-medium"
                  >
                    {{ item.clicks }}
                  </td>
                  <td class="py-3 px-4 text-sm text-n-slate-11">
                    {{ item.city || '-' }}
                  </td>
                  <td class="py-3 px-4 text-sm text-n-slate-11">
                    {{ item.region || '-' }}
                  </td>
                  <td class="py-3 px-4 text-sm text-n-slate-11">
                    {{ item.country || '-' }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="text-center py-8 text-n-slate-11">
            {{ $t('LEADS_REPORTS.NO_CLICKS_DATA') }}
          </div>
        </div>

        <!-- Clicks by Region Table -->
        <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak mb-6">
          <h3 class="text-lg font-semibold mb-4 text-n-slate-12">
            {{ $t('LEADS_REPORTS.CLICKS_BY_REGION') }}
          </h3>
          <div v-if="clicksByRegion.length" class="overflow-x-auto">
            <table class="w-full">
              <thead>
                <tr class="border-b border-n-weak">
                  <th
                    class="text-left py-3 px-4 text-sm font-medium text-n-slate-11"
                  >
                    {{ $t('LEADS_REPORTS.CLICKS_TABLE.REGION') }}
                  </th>
                  <th
                    class="text-left py-3 px-4 text-sm font-medium text-n-slate-11"
                  >
                    {{ $t('LEADS_REPORTS.CLICKS_TABLE.COUNTRY') }}
                  </th>
                  <th
                    class="text-right py-3 px-4 text-sm font-medium text-n-slate-11"
                  >
                    {{ $t('LEADS_REPORTS.CLICKS_TABLE.CLICKS') }}
                  </th>
                  <th
                    class="text-right py-3 px-4 text-sm font-medium text-n-slate-11"
                  >
                    {{ $t('LEADS_REPORTS.CLICKS_TABLE.UNIQUE_IPS') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(item, index) in clicksByRegion"
                  :key="index"
                  class="border-b border-n-weak hover:bg-n-alpha-2"
                >
                  <td class="py-3 px-4 text-sm text-n-slate-12">
                    {{ item.region }}
                  </td>
                  <td class="py-3 px-4 text-sm text-n-slate-11">
                    {{ item.country || '-' }}
                  </td>
                  <td
                    class="py-3 px-4 text-sm text-n-slate-12 text-right font-medium"
                  >
                    {{ item.clicks }}
                  </td>
                  <td class="py-3 px-4 text-sm text-n-slate-11 text-right">
                    {{ item.unique_ips }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="text-center py-8 text-n-slate-11">
            {{ $t('LEADS_REPORTS.NO_CLICKS_DATA') }}
          </div>
        </div>

        <!-- Clicks by Strategy Table -->
        <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
          <h3 class="text-lg font-semibold mb-4 text-n-slate-12">
            {{ $t('LEADS_REPORTS.CLICKS_BY_STRATEGY') }}
          </h3>
          <div v-if="clicksByStrategy.length" class="overflow-x-auto">
            <table class="w-full">
              <thead>
                <tr class="border-b border-n-weak">
                  <th
                    class="text-left py-3 px-4 text-sm font-medium text-n-slate-11"
                  >
                    {{ $t('LEADS_REPORTS.CLICKS_TABLE.STRATEGY') }}
                  </th>
                  <th
                    class="text-left py-3 px-4 text-sm font-medium text-n-slate-11"
                  >
                    {{ $t('LEADS_REPORTS.CLICKS_TABLE.CAMPAIGN_ID') }}
                  </th>
                  <th
                    class="text-right py-3 px-4 text-sm font-medium text-n-slate-11"
                  >
                    {{ $t('LEADS_REPORTS.CLICKS_TABLE.CLICKS') }}
                  </th>
                  <th
                    class="text-right py-3 px-4 text-sm font-medium text-n-slate-11"
                  >
                    {{ $t('LEADS_REPORTS.CLICKS_TABLE.UNIQUE_IPS') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="item in clicksByStrategy"
                  :key="item.strategy_id"
                  class="border-b border-n-weak hover:bg-n-alpha-2"
                >
                  <td class="py-3 px-4 text-sm text-n-slate-12">
                    {{ item.strategy_name }}
                  </td>
                  <td class="py-3 px-4 text-sm text-n-slate-11">
                    {{ item.id_campanha || '-' }}
                  </td>
                  <td
                    class="py-3 px-4 text-sm text-n-slate-12 text-right font-medium"
                  >
                    {{ item.clicks }}
                  </td>
                  <td class="py-3 px-4 text-sm text-n-slate-11 text-right">
                    {{ item.unique_ips }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="text-center py-8 text-n-slate-11">
            {{ $t('LEADS_REPORTS.NO_CLICKS_DATA') }}
          </div>
        </div>
      </div>

      <div v-else class="text-center py-12 text-n-slate-11">
        {{ $t('LEADS_REPORTS.NO_CLICKS_DATA') }}
      </div>
    </template>

    <!-- ==================== CAMPAIGNS TAB ==================== -->
    <template v-if="activeTab === 'campaigns'">
      <!-- Filters -->
      <div class="grid grid-cols-1 gap-4 mb-6 md:grid-cols-5">
        <div class="multiselect-wrap--small">
          <p class="mb-2 text-xs font-medium">Caixa de entrada</p>
          <select
            :value="selectedCampaignInbox?.id ?? ''"
            class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12"
            @change="onCampaignInboxSelect"
          >
            <option value="">Todas as caixas</option>
            <option
              v-for="inbox in inboxes"
              :key="inbox.id"
              :value="inbox.id"
            >
              {{ inbox.name }}
            </option>
          </select>
        </div>
        <div class="multiselect-wrap--small">
          <p class="mb-2 text-xs font-medium">Agente</p>
          <select
            :value="selectedAgent ?? ''"
            class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12"
            @change="onAgentSelect"
          >
            <option
              v-for="a in agentOptions"
              :key="a.id"
              :value="a.id ?? ''"
            >
              {{ a.name }}
            </option>
          </select>
        </div>
        <div class="multiselect-wrap--small">
          <p class="mb-2 text-xs font-medium">Código LP</p>
          <select
            :value="selectedLpCode ?? ''"
            class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12"
            @change="onLpCodeSelect"
          >
            <option value="">
              Todos os códigos ({{ lpCodeOptions.length }})
            </option>
            <option
              v-for="lp in lpCodeOptions"
              :key="lp.id"
              :value="lp.id"
            >
              {{ lp.name }}
            </option>
          </select>
        </div>
        <div class="md:col-span-2">
          <p class="mb-2 text-xs font-medium">Período</p>
          <WootDateRangePicker
            show-range
            :value="campaignDateRange"
            :confirm-text="$t('REPORT.CUSTOM_DATE_RANGE.CONFIRM')"
            placeholder="Selecione o período"
            @change="onCampaignDateChange"
          />
        </div>
      </div>

      <div
        v-if="isCampaignLoading"
        class="flex justify-center items-center py-12"
      >
        <Spinner />
      </div>

      <div v-else-if="campaignData">
        <!-- Summary -->
        <div class="grid grid-cols-2 gap-4 mb-6 md:grid-cols-5">
          <div class="p-4 rounded-lg border bg-n-solid-2 border-n-weak">
            <p class="mb-1 text-sm text-n-slate-11">Total de leads</p>
            <p class="text-2xl font-semibold text-n-slate-12">
              {{ campaignTotals.total_leads || 0 }}
            </p>
          </div>
          <div class="p-4 rounded-lg border bg-n-solid-2 border-n-weak">
            <p class="mb-1 text-sm text-n-slate-11">Campanhas detectadas</p>
            <p class="text-2xl font-semibold text-n-blue-11">
              {{ campaignTotals.total_campaigns || 0 }}
            </p>
          </div>
          <div class="p-4 rounded-lg border bg-n-solid-2 border-n-weak">
            <p class="mb-1 text-sm text-n-slate-11">Códigos LP distintos</p>
            <p class="text-2xl font-semibold text-n-teal-11">
              {{ lpCodeOptions.length }}
            </p>
          </div>
          <div class="p-4 rounded-lg border bg-n-solid-2 border-n-weak">
            <p class="mb-1 text-sm text-n-slate-11">Com código LP</p>
            <p class="text-2xl font-semibold text-n-teal-11">
              {{ lpCodeSummary.withCode }}
            </p>
          </div>
          <div class="p-4 rounded-lg border bg-n-solid-2 border-n-weak">
            <p class="mb-1 text-sm text-n-slate-11">Sem código LP</p>
            <p class="text-2xl font-semibold text-n-slate-11">
              {{ lpCodeSummary.withoutCode }}
            </p>
          </div>
        </div>

        <!-- Traffic Origin Table -->
        <div class="p-4 mb-6 rounded-lg border bg-n-solid-2 border-n-weak">
          <h3 class="mb-4 text-lg font-semibold text-n-slate-12">
            Origem do tráfego
          </h3>
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead>
                <tr class="border-b border-n-weak">
                  <th
                    class="py-3 px-4 text-sm font-medium text-left text-n-slate-11"
                  >
                    Origem
                  </th>
                  <th
                    class="py-3 px-4 text-sm font-medium text-right text-n-slate-11"
                  >
                    Total
                  </th>
                  <th
                    class="py-3 px-4 text-sm font-medium text-right text-n-slate-11"
                  >
                    Com LP
                  </th>
                  <th
                    class="py-3 px-4 text-sm font-medium text-right text-n-slate-11"
                  >
                    Sem LP
                  </th>
                  <th
                    class="py-3 px-4 text-sm font-medium text-right text-n-slate-11"
                  >
                    Sem venda
                  </th>
                  <th
                    class="py-3 px-4 text-sm font-medium text-right text-n-slate-11"
                  >
                    Em tratamento
                  </th>
                  <th
                    class="py-3 px-4 text-sm font-medium text-right text-n-slate-11"
                  >
                    Finalizado
                  </th>
                  <th
                    class="py-3 px-4 text-sm font-medium text-right text-n-slate-11"
                  >
                    Sem status
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="origin in trafficOriginSummary"
                  :key="origin.label"
                  class="border-b border-n-weak hover:bg-n-alpha-2"
                >
                  <td class="py-3 px-4 text-sm font-medium text-n-slate-12">
                    {{ origin.label }}
                  </td>
                  <td
                    class="py-3 px-4 text-sm font-medium text-right text-n-blue-11"
                  >
                    {{ origin.total }}
                  </td>
                  <td
                    class="py-3 px-4 text-sm font-medium text-right text-n-teal-11"
                  >
                    {{ origin.comLp }}
                  </td>
                  <td
                    class="py-3 px-4 text-sm font-medium text-right text-n-slate-11"
                  >
                    {{ origin.semLp }}
                  </td>
                  <td
                    class="py-3 px-4 text-sm font-medium text-right text-n-amber-11"
                  >
                    {{ origin.sem_venda }}
                  </td>
                  <td
                    class="py-3 px-4 text-sm font-medium text-right text-n-blue-11"
                  >
                    {{ origin.em_tratamento }}
                  </td>
                  <td
                    class="py-3 px-4 text-sm font-medium text-right text-n-teal-11"
                  >
                    {{ origin.finalizado }}
                  </td>
                  <td
                    class="py-3 px-4 text-sm font-medium text-right text-n-slate-9"
                  >
                    {{ origin.sem_status }}
                  </td>
                </tr>
                <tr class="border-t-2 border-n-weak bg-n-alpha-1">
                  <td class="py-3 px-4 text-sm font-semibold text-n-slate-12">
                    Total
                  </td>
                  <td
                    class="py-3 px-4 text-sm font-semibold text-right text-n-blue-11"
                  >
                    {{ campaignTotals.total_leads || 0 }}
                  </td>
                  <td
                    class="py-3 px-4 text-sm font-semibold text-right text-n-teal-11"
                  >
                    {{ lpCodeSummary.withCode }}
                  </td>
                  <td
                    class="py-3 px-4 text-sm font-semibold text-right text-n-slate-11"
                  >
                    {{ lpCodeSummary.withoutCode }}
                  </td>
                  <td
                    class="py-3 px-4 text-sm font-semibold text-right text-n-amber-11"
                  >
                    {{ statusTotals.sem_venda }}
                  </td>
                  <td
                    class="py-3 px-4 text-sm font-semibold text-right text-n-blue-11"
                  >
                    {{ statusTotals.em_tratamento }}
                  </td>
                  <td
                    class="py-3 px-4 text-sm font-semibold text-right text-n-teal-11"
                  >
                    {{ statusTotals.finalizado }}
                  </td>
                  <td
                    class="py-3 px-4 text-sm font-semibold text-right text-n-slate-9"
                  >
                    {{ statusTotals.sem_status }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- LP Code Breakdown Table -->
        <div
          v-if="lpCodeSummary.rows.length"
          class="p-4 mb-6 rounded-lg border bg-n-solid-2 border-n-weak"
        >
          <h3 class="mb-4 text-lg font-semibold text-n-slate-12">
            Subtotal por código LP
          </h3>
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead>
                <tr class="border-b border-n-weak">
                  <th
                    class="py-3 px-4 text-sm font-medium text-left text-n-slate-11"
                  >
                    Código LP
                  </th>
                  <th
                    class="py-3 px-4 text-sm font-medium text-right text-n-slate-11"
                  >
                    Leads
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in lpCodeSummary.rows"
                  :key="row.code"
                  class="border-b border-n-weak hover:bg-n-alpha-2 cursor-pointer"
                  @click="toggleLpCodeFilter(row.code)"
                >
                  <td class="py-3 px-4 text-sm font-mono font-medium text-n-slate-12">
                    <span
                      class="inline-block py-0.5 px-2 mr-2 text-xs rounded-full"
                      :class="selectedLpCode === row.code ? 'bg-n-blue-9 text-white' : 'bg-n-slate-3 text-n-slate-12'"
                    >
                      {{ row.code }}
                    </span>
                  </td>
                  <td
                    class="py-3 px-4 text-sm font-medium text-right text-n-blue-11"
                  >
                    {{ row.count }}
                  </td>
                </tr>
                <tr class="border-t-2 border-n-weak">
                  <td class="py-3 px-4 text-sm font-medium text-n-slate-11">
                    Sem código
                  </td>
                  <td
                    class="py-3 px-4 text-sm font-medium text-right text-n-slate-11"
                  >
                    {{ lpCodeSummary.withoutCode }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Heatmap: Mapa de calor de leads por dia/hora -->
        <div class="p-4 mb-6 rounded-lg border bg-n-solid-2 border-n-weak">
          <h3 class="mb-4 text-lg font-semibold text-n-slate-12">
            Mapa de calor — Leads por dia e horário
          </h3>
          <div
            v-if="isHeatmapLoading"
            class="flex justify-center items-center py-12"
          >
            <Spinner />
          </div>
          <div v-else-if="heatmapData" class="overflow-x-auto">
            <div class="min-w-[700px]">
              <!-- Header: horas -->
              <div
                class="grid gap-[3px] mb-1"
                style="grid-template-columns: 80px repeat(24, 1fr)"
              >
                <div />
                <div
                  v-for="h in 24"
                  :key="h"
                  class="text-[9px] font-semibold text-center text-n-slate-11"
                >
                  {{ h - 1 }}h
                </div>
              </div>
              <!-- Rows: dias da semana -->
              <div
                v-for="(row, dayIndex) in heatmapData"
                :key="dayIndex"
                class="grid gap-[3px] mb-[3px]"
                style="grid-template-columns: 80px repeat(24, 1fr)"
              >
                <div
                  class="flex items-center justify-end pr-2 text-[11px] font-semibold text-n-slate-12"
                >
                  {{ DIAS_SEMANA[dayIndex] }}
                </div>
                <div
                  v-for="(value, hour) in row"
                  :key="hour"
                  class="h-8 rounded-sm cursor-pointer transition-all duration-150"
                  :class="heatmapCellColor(value)"
                  @mouseenter="
                    showHeatmapTooltip($event, dayIndex, hour, value)
                  "
                  @mouseleave="hideHeatmapTooltip"
                >
                  <div
                    v-if="value > 0"
                    class="flex justify-center items-center w-full h-full text-[9px] font-bold text-white/80"
                  >
                    {{ value }}
                  </div>
                </div>
              </div>
            </div>
            <!-- Legenda -->
            <div class="flex gap-2 items-center mt-4">
              <span class="text-[10px] text-n-slate-11">Menos</span>
              <div
                class="w-4 h-4 rounded-sm bg-n-slate-2 dark:bg-n-slate-1/30"
              />
              <div class="w-4 h-4 rounded-sm bg-n-blue-3" />
              <div class="w-4 h-4 rounded-sm bg-n-blue-5" />
              <div class="w-4 h-4 rounded-sm bg-n-blue-7" />
              <div class="w-4 h-4 rounded-sm bg-n-blue-9" />
              <div class="w-4 h-4 rounded-sm bg-n-blue-10" />
              <div class="w-4 h-4 rounded-sm bg-n-blue-11" />
              <span class="text-[10px] text-n-slate-11">Mais</span>
            </div>
          </div>
          <div v-else class="py-8 text-center text-n-slate-11">
            Nenhum dado disponível para o período selecionado.
          </div>
        </div>

        <!-- Tooltip do heatmap -->
        <div
          v-if="heatmapTooltip.visible"
          class="fixed z-50 px-2 py-1 text-xs font-medium rounded shadow-lg pointer-events-none text-n-slate-6 bg-n-slate-12"
          :style="{
            left: `${heatmapTooltip.x}px`,
            top: `${heatmapTooltip.y - 15}px`,
            transform: 'translateX(-50%)',
          }"
        >
          {{ heatmapTooltip.text }}
        </div>

        <!-- Lead Webhook Inboxes -->
        <div class="p-4 mb-6 rounded-lg border bg-n-solid-2 border-n-weak">
          <h3 class="mb-3 text-sm font-semibold text-n-slate-12">
            Caixas de entrada para análise de leads
          </h3>
          <p class="mb-3 text-xs text-n-slate-10">
            Selecione quais caixas de entrada devem ser analisadas para
            detecção automática de leads. Apenas conversas dessas caixas
            serão enviadas ao webhook de leads.
          </p>
          <div class="flex flex-wrap gap-2 mb-4">
            <button
              v-for="inbox in inboxes"
              :key="'lead-inbox-' + inbox.id"
              class="flex gap-2 items-center py-1.5 px-3 text-sm font-medium rounded-lg border transition-colors"
              :class="
                isInboxSelectedForLeads(inbox.id)
                  ? 'bg-n-blue-9 text-white border-n-blue-9'
                  : 'bg-n-solid-2 text-n-slate-11 border-n-weak hover:border-n-blue-7'
              "
              @click="toggleLeadInbox(inbox)"
            >
              <span
                class="flex justify-center items-center w-4 h-4 rounded border"
                :class="
                  isInboxSelectedForLeads(inbox.id)
                    ? 'bg-white/20 border-white/40'
                    : 'border-n-slate-7'
                "
              >
                <span
                  v-if="isInboxSelectedForLeads(inbox.id)"
                  class="text-xs text-white"
                >
                  &#10003;
                </span>
              </span>
              {{ inbox.name }}
            </button>
          </div>
          <div class="flex gap-3 items-center">
            <button
              class="flex gap-2 items-center py-1.5 px-4 text-sm font-medium text-white rounded-lg transition-colors"
              :class="
                isSavingLeadInboxes
                  ? 'bg-n-slate-8 cursor-not-allowed'
                  : 'bg-n-blue-9 hover:bg-n-blue-10'
              "
              :disabled="isSavingLeadInboxes"
              @click="saveLeadWebhookInboxes"
            >
              <Spinner v-if="isSavingLeadInboxes" size="tiny" />
              <span>Salvar inboxes</span>
            </button>
            <p v-if="leadInboxesSaved" class="text-xs text-n-teal-11">
              Inboxes atualizados com sucesso. As regras de automação foram
              atualizadas.
            </p>
            <p
              v-if="!leadWebhookInboxes.length && !isSavingLeadInboxes"
              class="text-xs text-n-amber-11"
            >
              Nenhuma caixa selecionada. Leads não serão detectados
              automaticamente.
            </p>
          </div>
        </div>

        <!-- Sync Webhook -->
        <div class="p-4 mb-6 rounded-lg border bg-n-solid-2 border-n-weak">
          <h3 class="mb-3 text-sm font-semibold text-n-slate-12">
            Sincronizar com webhook
          </h3>
          <div class="flex flex-wrap gap-3 items-end">
            <div class="flex-1 min-w-[300px]">
              <label class="block mb-1 text-xs font-medium text-n-slate-11">
                URL do webhook
              </label>
              <input
                v-model="webhookUrl"
                type="url"
                placeholder="Cole a URL do webhook aqui para habilitar a sincronização"
                class="py-1.5 px-3 w-full text-sm rounded-lg border border-n-weak bg-n-alpha-black1 text-n-slate-12"
              />
            </div>
            <button
              class="flex gap-2 items-center py-1.5 px-4 text-sm font-medium text-white rounded-lg transition-colors"
              :class="
                isSavingWebhook
                  ? 'bg-n-slate-8 cursor-not-allowed'
                  : 'bg-n-slate-12 hover:bg-n-slate-11'
              "
              :disabled="isSavingWebhook"
              @click="saveWebhookUrl"
            >
              <Spinner v-if="isSavingWebhook" size="tiny" />
              <span>Salvar URL</span>
            </button>
            <button
              class="flex gap-2 items-center py-1.5 px-4 text-sm font-medium text-white rounded-lg transition-colors"
              :class="
                isSyncing || !campaignLeads.length || !webhookUrl.trim()
                  ? 'bg-n-slate-8 cursor-not-allowed'
                  : 'bg-n-blue-9 hover:bg-n-blue-10'
              "
              :disabled="
                isSyncing || !campaignLeads.length || !webhookUrl.trim()
              "
              @click="syncCampaignLeads"
            >
              <Spinner v-if="isSyncing" size="tiny" />
              <span>
                Sincronizar {{ campaignLeads.length }} leads
              </span>
            </button>
          </div>
          <p v-if="webhookSaved" class="mt-2 text-xs text-n-teal-11">
            URL salva com sucesso.
          </p>
          <p v-if="!webhookUrl.trim()" class="mt-2 text-xs text-n-amber-11">
            Informe e salve a URL do webhook para habilitar a sincronização.
          </p>
          <p v-else class="mt-2 text-xs text-n-slate-10">
            Envia todos os leads do período para o webhook informado.
          </p>

          <!-- Sync Processing -->
          <div
            v-if="syncResult && syncResult.queued && !syncResult.finished_at"
            class="p-3 mt-3 rounded-lg bg-n-amber-2"
          >
            <p class="text-sm font-medium text-n-amber-11">
              <Spinner size="tiny" class="inline mr-1" />
              Sincronizando em background... aguarde.
            </p>
          </div>

          <!-- Sync Result -->
          <div
            v-if="syncResult && syncResult.finished_at && !syncResult.error"
            class="p-3 mt-3 rounded-lg bg-n-teal-2"
          >
            <p class="text-sm font-medium text-n-teal-11">
              Sincronização concluída:
              {{ syncResult.synced }} enviados, {{ syncResult.skipped }} sem
              código (ignorados)
              <template v-if="syncResult.already_synced">
                , {{ syncResult.already_synced }} já confirmados (mensagem privada recebida)
              </template>
              <template v-if="syncResult.errors_count">
                , {{ syncResult.errors_count }} erros
              </template>
            </p>
            <p class="mt-1 text-xs text-n-teal-10">
              Webhook: {{ syncResult.webhook_url }} | Sincronizado em:
              {{ formatLastRun(syncResult.synced_at) }}
            </p>
            <div
              v-if="syncResult.errors && syncResult.errors.length"
              class="mt-2"
            >
              <p
                v-for="(err, idx) in syncResult.errors"
                :key="idx"
                class="text-xs text-n-ruby-11"
              >
                {{ err.name }} ({{ err.phone }}): {{ err.error }}
              </p>
            </div>
          </div>
          <div
            v-if="syncResult && syncResult.error"
            class="p-3 mt-3 rounded-lg bg-n-ruby-2"
          >
            <p class="text-sm font-medium text-n-ruby-11">
              Erro ao sincronizar. Verifique a URL e tente novamente.
            </p>
            <p
              v-if="syncResult.message"
              class="mt-1 text-xs text-n-ruby-10"
            >
              {{ syncResult.message }}
            </p>
          </div>

          <!-- Auto-Sync Settings -->
          <div class="pt-4 mt-4 border-t border-n-weak">
            <h4 class="mb-3 text-sm font-semibold text-n-slate-12">
              Sincronização automática
            </h4>
            <div class="flex flex-wrap gap-4 items-end">
              <label class="flex gap-2 items-center cursor-pointer">
                <input
                  v-model="autoSyncEnabled"
                  type="checkbox"
                  class="w-4 h-4 rounded border accent-n-blue-9 border-n-weak"
                />
                <span class="text-sm text-n-slate-12">Ativar auto-sync</span>
              </label>
              <div class="multiselect-wrap--small min-w-[220px]">
                <select
                  :value="selectedAutoSyncInterval?.id ?? ''"
                  class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12"
                  :disabled="!autoSyncEnabled"
                  @change="onAutoSyncIntervalSelect"
                >
                  <option
                    v-for="o in autoSyncIntervalOptions"
                    :key="o.id"
                    :value="o.id"
                  >
                    {{ o.name }}
                  </option>
                </select>
              </div>
              <button
                class="flex gap-2 items-center py-1.5 px-4 text-sm font-medium text-white rounded-lg transition-colors"
                :class="
                  isSavingAutoSync
                    ? 'bg-n-slate-8 cursor-not-allowed'
                    : 'bg-n-slate-12 hover:bg-n-slate-11'
                "
                :disabled="isSavingAutoSync"
                @click="saveAutoSyncSettings"
              >
                <Spinner v-if="isSavingAutoSync" size="tiny" />
                <span>Salvar</span>
              </button>
            </div>
            <p v-if="autoSyncSaved" class="mt-2 text-xs text-n-teal-11">
              Configuração salva com sucesso.
            </p>
            <p class="mt-2 text-xs text-n-slate-10">
              <template v-if="autoSyncEnabled">
                Sincronização ativa — novos leads com código serão enviados
                automaticamente ao webhook.
              </template>
              <template v-else>
                Desativado — leads não serão sincronizados automaticamente.
              </template>
              <span v-if="autoSyncLastRun" class="ml-2">
                Última execução: {{ formatLastRun(autoSyncLastRun) }}
              </span>
            </p>
          </div>
        </div>

        <!-- Campaign Summary Cards -->
        <div
          v-if="campaignSummary.length"
          class="p-4 mb-6 rounded-lg border bg-n-solid-2 border-n-weak"
        >
          <h3 class="mb-4 text-lg font-semibold text-n-slate-12">
            Subtotal por campanha
          </h3>
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead>
                <tr class="border-b border-n-weak">
                  <th
                    class="py-3 px-4 text-sm font-medium text-left text-n-slate-11"
                  >
                    Campanha
                  </th>
                  <th
                    class="py-3 px-4 text-sm font-medium text-right text-n-slate-11"
                  >
                    Leads
                  </th>
                  <th
                    class="py-3 px-4 text-sm font-medium text-left text-n-slate-11"
                  >
                    Procedimentos
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="camp in campaignSummary"
                  :key="camp.campaign_key"
                  class="border-b border-n-weak hover:bg-n-alpha-2"
                >
                  <td class="py-3 px-4 text-sm font-medium text-n-slate-12">
                    {{ camp.campaign }}
                  </td>
                  <td
                    class="py-3 px-4 text-sm font-medium text-right text-n-blue-11"
                  >
                    {{ camp.count }}
                  </td>
                  <td class="py-3 px-4 text-sm text-n-slate-11">
                    <span
                      v-for="(proc, idx) in camp.procedures.slice(0, 5)"
                      :key="idx"
                      class="inline-block py-0.5 px-2 mb-1 mr-1 text-xs rounded-full bg-n-slate-3 text-n-slate-12"
                    >
                      {{ proc.name }} ({{ proc.count }})
                    </span>
                    <span
                      v-if="camp.procedures.length > 5"
                      class="text-xs text-n-slate-10"
                    >
                      +{{ camp.procedures.length - 5 }} mais
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Agent Summary Table -->
        <div
          v-if="agentSummary.length"
          class="p-4 mb-6 rounded-lg border bg-n-solid-2 border-n-weak"
        >
          <h3 class="mb-4 text-lg font-semibold text-n-slate-12">
            Subtotal por agente
          </h3>
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead>
                <tr class="border-b border-n-weak">
                  <th
                    class="py-3 px-4 text-sm font-medium text-left text-n-slate-11"
                  >
                    Agente
                  </th>
                  <th
                    class="py-3 px-4 text-sm font-medium text-right text-n-slate-11"
                  >
                    Leads
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="agent in agentSummary"
                  :key="agent.agent"
                  class="border-b border-n-weak hover:bg-n-alpha-2"
                >
                  <td class="py-3 px-4 text-sm font-medium text-n-slate-12">
                    {{ agent.agent }}
                  </td>
                  <td
                    class="py-3 px-4 text-sm font-medium text-right text-n-blue-11"
                  >
                    {{ agent.count }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Leads Detail Table -->
        <div class="p-4 rounded-lg border bg-n-solid-2 border-n-weak">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-lg font-semibold text-n-slate-12">
              Detalhamento de leads ({{ campaignLeads.length }})
            </h3>
            <input
              v-model="leadSearchQuery"
              type="text"
              placeholder="Buscar por nome, telefone, código, campanha..."
              class="px-3 py-2 text-sm rounded-lg border w-80 border-n-weak bg-n-solid-3 text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-n-brand"
              @input="currentPage = 1"
            />
          </div>
          <div v-if="campaignLeads.length">
            <table class="w-full table-fixed">
              <thead>
                <tr class="border-b border-n-weak">
                  <th
                    class="py-2 px-2 text-xs font-medium text-left text-n-slate-11 w-[15%]"
                  >
                    Nome
                  </th>
                  <th
                    class="py-2 px-2 text-xs font-medium text-left text-n-slate-11 w-[12%]"
                  >
                    Telefone
                  </th>
                  <th
                    class="py-2 px-2 text-xs font-medium text-left text-n-slate-11 w-[9%]"
                  >
                    Caixa
                  </th>
                  <th
                    class="py-2 px-2 text-xs font-medium text-left text-n-slate-11 w-[8%]"
                  >
                    Agente
                  </th>
                  <th
                    class="py-2 px-2 text-xs font-medium text-left text-n-slate-11 w-[18%]"
                  >
                    Campanha
                  </th>
                  <th
                    class="py-2 px-2 text-xs font-medium text-left text-n-slate-11 w-[12%]"
                  >
                    Procedimento
                  </th>
                  <th
                    class="py-2 px-2 text-xs font-medium text-left text-n-slate-11 w-[9%]"
                  >
                    Código LP
                  </th>
                  <th
                    class="py-2 px-2 text-xs font-medium text-left text-n-slate-11 w-[13%]"
                  >
                    Data
                  </th>
                  <th
                    class="py-2 px-2 text-xs font-medium text-center text-n-slate-11 w-[4%]"
                  >
                    Sync
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="lead in paginatedLeads"
                  :key="lead.conversation_id"
                  class="border-b border-n-weak hover:bg-n-alpha-2"
                >
                  <td class="py-2 px-2 text-xs text-n-slate-12 truncate" :title="lead.name">
                    {{ lead.name }}
                  </td>
                  <td class="py-2 px-2 text-xs font-mono text-n-slate-11 truncate">
                    {{ lead.phone }}
                  </td>
                  <td class="py-2 px-2 text-xs text-n-slate-11 truncate">
                    {{ lead.inbox || '-' }}
                  </td>
                  <td class="py-2 px-2 text-xs text-n-slate-11 truncate">
                    {{ lead.agent || '-' }}
                  </td>
                  <td class="py-2 px-2 text-xs text-n-slate-12 truncate" :title="lead.campaign">
                    {{ lead.campaign }}
                  </td>
                  <td class="py-2 px-2 text-xs text-n-slate-11 truncate">
                    {{ lead.procedure || '-' }}
                  </td>
                  <td class="py-2 px-2 text-xs font-mono text-n-slate-11 truncate">
                    {{ lead.campaign_code || '-' }}
                  </td>
                  <td
                    class="py-2 px-2 text-xs whitespace-nowrap text-n-slate-11"
                  >
                    {{ lead.date }}
                  </td>
                  <td class="py-2 px-2 text-xs text-center">
                    <span
                      v-if="lead.synced"
                      class="text-n-teal-11"
                      title="Sincronizado com webhook"
                    >&#10003;</span>
                    <span v-else-if="lead.campaign_code" class="text-n-slate-9">-</span>
                    <span v-else class="text-n-slate-7">-</span>
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- Pagination -->
            <div
              v-if="totalPages > 1"
              class="flex items-center justify-between pt-4 mt-4 border-t border-n-weak"
            >
              <p class="text-xs text-n-slate-11">
                Mostrando
                {{ (currentPage - 1) * pageSize + 1 }}-{{
                  Math.min(currentPage * pageSize, campaignLeads.length)
                }}
                de {{ campaignLeads.length }} leads
              </p>
              <div class="flex items-center gap-1">
                <button
                  class="px-2 py-1 text-xs font-medium rounded transition-colors"
                  :class="
                    currentPage === 1
                      ? 'text-n-slate-9 cursor-not-allowed'
                      : 'text-n-slate-12 hover:bg-n-alpha-2'
                  "
                  :disabled="currentPage === 1"
                  @click="goToPage(currentPage - 1)"
                >
                  Anterior
                </button>
                <template v-for="page in totalPages" :key="page">
                  <button
                    v-if="
                      page === 1 ||
                      page === totalPages ||
                      Math.abs(page - currentPage) <= 2
                    "
                    class="px-2.5 py-1 text-xs font-medium rounded transition-colors"
                    :class="
                      page === currentPage
                        ? 'bg-n-blue-9 text-white'
                        : 'text-n-slate-12 hover:bg-n-alpha-2'
                    "
                    @click="goToPage(page)"
                  >
                    {{ page }}
                  </button>
                  <span
                    v-else-if="
                      Math.abs(page - currentPage) === 3 &&
                      page !== 1 &&
                      page !== totalPages
                    "
                    class="px-1 text-xs text-n-slate-9"
                  >
                    ...
                  </span>
                </template>
                <button
                  class="px-2 py-1 text-xs font-medium rounded transition-colors"
                  :class="
                    currentPage === totalPages
                      ? 'text-n-slate-9 cursor-not-allowed'
                      : 'text-n-slate-12 hover:bg-n-alpha-2'
                  "
                  :disabled="currentPage === totalPages"
                  @click="goToPage(currentPage + 1)"
                >
                  Próxima
                </button>
              </div>
            </div>
          </div>
          <div v-else class="py-8 text-center text-n-slate-11">
            Nenhum lead de campanha encontrado no período.
          </div>
        </div>
      </div>

      <div v-else class="py-12 text-center text-n-slate-11">
        Selecione um período para visualizar os leads de campanha.
      </div>
    </template>

    <!-- ==================== MANAGERS TAB ==================== -->
    <template v-if="activeTab === 'managers'">
      <div
        v-if="isManagersLoading"
        class="flex justify-center items-center py-12"
      >
        <Spinner />
      </div>

      <div v-else>
        <!-- Configuração da Caixa de Entrada -->
        <div class="p-4 mb-6 rounded-lg border bg-n-solid-2 border-n-weak">
          <h3 class="mb-3 text-sm font-semibold text-n-slate-12">
            Configurações de envio
          </h3>
          <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div class="multiselect-wrap--small">
              <p class="mb-2 text-xs font-medium text-n-slate-11">
                Caixa de entrada para envio
              </p>
              <select
                :value="selectedManagerInbox?.id || ''"
                class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12"
                @change="onManagerInboxSelect"
              >
                <option value="" disabled>
                  Selecione ({{ managerInboxOptions.length }} caixas)
                </option>
                <option
                  v-for="inbox in managerInboxOptions"
                  :key="inbox.id"
                  :value="inbox.id"
                >
                  {{ inbox.name }}
                </option>
              </select>
            </div>
            <div class="flex items-end gap-3">
              <label class="flex gap-2 items-center cursor-pointer">
                <input
                  v-model="managerSettings.notify_realtime"
                  type="checkbox"
                  class="w-4 h-4 rounded border accent-n-blue-9 border-n-weak"
                />
                <span class="text-sm text-n-slate-12">
                  Notificar em tempo real (a cada novo lead)
                </span>
              </label>
            </div>
          </div>

          <!-- Digest Settings -->
          <div class="pt-4 mt-4 border-t border-n-weak">
            <div class="flex gap-2 items-center mb-3">
              <label class="flex gap-2 items-center cursor-pointer">
                <input
                  v-model="managerSettings.digest_enabled"
                  type="checkbox"
                  class="w-4 h-4 rounded border accent-n-blue-9 border-n-weak"
                />
                <span class="text-sm font-semibold text-n-slate-12">
                  Resumo automático de leads
                </span>
              </label>
            </div>

            <div v-if="managerSettings.digest_enabled" class="space-y-4">
              <!-- Days of week -->
              <div>
                <p class="mb-2 text-xs font-medium text-n-slate-11">
                  Dias da semana
                </p>
                <div class="flex flex-wrap gap-2">
                  <button
                    v-for="day in DIAS_SEMANA_LABELS"
                    :key="day.id"
                    class="py-1 px-3 text-xs font-medium rounded-full border transition-colors"
                    :class="
                      (managerSettings.digest_days || []).includes(day.id)
                        ? 'bg-n-blue-9 text-white border-n-blue-9'
                        : 'bg-n-solid-2 text-n-slate-11 border-n-weak hover:border-n-blue-7'
                    "
                    @click="toggleDigestDay(day.id)"
                  >
                    {{ day.name }}
                  </button>
                </div>
              </div>

              <!-- Hours -->
              <div>
                <p class="mb-2 text-xs font-medium text-n-slate-11">
                  Horários de envio
                </p>
                <div class="flex flex-wrap gap-2">
                  <button
                    v-for="hour in HOUR_OPTIONS"
                    :key="hour"
                    class="py-1 px-2 text-xs font-medium rounded-lg border transition-colors"
                    :class="
                      (managerSettings.digest_hours || []).includes(hour)
                        ? 'bg-n-blue-9 text-white border-n-blue-9'
                        : 'bg-n-solid-2 text-n-slate-11 border-n-weak hover:border-n-blue-7'
                    "
                    @click="toggleDigestHour(hour)"
                  >
                    {{ hour }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Save button -->
          <div class="flex gap-3 items-center mt-4">
            <button
              class="flex gap-2 items-center py-1.5 px-4 text-sm font-medium text-white rounded-lg transition-colors"
              :class="
                isSavingManagerSettings
                  ? 'bg-n-slate-8 cursor-not-allowed'
                  : 'bg-n-blue-9 hover:bg-n-blue-10'
              "
              :disabled="isSavingManagerSettings"
              @click="saveManagerSettings"
            >
              <Spinner v-if="isSavingManagerSettings" size="tiny" />
              <span>Salvar configurações</span>
            </button>
            <p v-if="managerSettingsSaved" class="text-xs text-n-teal-11">
              Configurações salvas com sucesso.
            </p>
          </div>
        </div>

        <!-- Cadastro de Gestores -->
        <div class="p-4 mb-6 rounded-lg border bg-n-solid-2 border-n-weak">
          <h3 class="mb-3 text-sm font-semibold text-n-slate-12">
            Gestores cadastrados
          </h3>

          <!-- Add Manager Form -->
          <div class="flex flex-wrap gap-3 items-end mb-4">
            <div class="flex-1 min-w-[200px]">
              <label class="block mb-1 text-xs font-medium text-n-slate-11">
                Nome
              </label>
              <input
                v-model="newManagerName"
                type="text"
                placeholder="Nome do gestor"
                class="py-1.5 px-3 w-full text-sm rounded-lg border border-n-weak bg-n-alpha-black1 text-n-slate-12"
              />
            </div>
            <div class="flex-1 min-w-[200px]">
              <label class="block mb-1 text-xs font-medium text-n-slate-11">
                Telefone (WhatsApp)
              </label>
              <input
                v-model="newManagerPhone"
                type="tel"
                placeholder="+5521999999999"
                class="py-1.5 px-3 w-full text-sm rounded-lg border border-n-weak bg-n-alpha-black1 text-n-slate-12"
              />
            </div>
            <button
              class="flex gap-2 items-center py-1.5 px-4 text-sm font-medium text-white rounded-lg transition-colors"
              :class="
                isSavingManager ||
                !newManagerName.trim() ||
                !newManagerPhone.trim()
                  ? 'bg-n-slate-8 cursor-not-allowed'
                  : 'bg-n-blue-9 hover:bg-n-blue-10'
              "
              :disabled="
                isSavingManager ||
                !newManagerName.trim() ||
                !newManagerPhone.trim()
              "
              @click="addManager"
            >
              <Spinner v-if="isSavingManager" size="tiny" />
              <span>Adicionar gestor</span>
            </button>
          </div>

          <!-- Managers List -->
          <div v-if="managers.length" class="overflow-x-auto">
            <table class="w-full">
              <thead>
                <tr class="border-b border-n-weak">
                  <th
                    class="py-3 px-4 text-sm font-medium text-left text-n-slate-11"
                  >
                    Nome
                  </th>
                  <th
                    class="py-3 px-4 text-sm font-medium text-left text-n-slate-11"
                  >
                    Telefone
                  </th>
                  <th
                    class="py-3 px-4 text-sm font-medium text-center text-n-slate-11"
                  >
                    Notificação
                  </th>
                  <th
                    class="py-3 px-4 text-sm font-medium text-center text-n-slate-11"
                  >
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="manager in managers"
                  :key="manager.id"
                  class="border-b border-n-weak hover:bg-n-alpha-2"
                >
                  <td class="py-3 px-4 text-sm text-n-slate-12">
                    {{ manager.name }}
                  </td>
                  <td class="py-3 px-4 text-sm font-mono text-n-slate-11">
                    {{ manager.phone }}
                  </td>
                  <td class="py-3 px-4 text-center">
                    <button
                      class="py-1 px-3 text-xs font-medium rounded-full transition-colors"
                      :class="
                        manager.active
                          ? 'bg-n-teal-3 text-n-teal-11'
                          : 'bg-n-slate-3 text-n-slate-10'
                      "
                      @click="toggleManager(manager)"
                    >
                      {{ manager.active ? 'Ativa' : 'Desativada' }}
                    </button>
                  </td>
                  <td class="py-3 px-4 text-center">
                    <button
                      class="py-1 px-3 text-xs font-medium rounded-full text-n-ruby-11 bg-n-ruby-3 hover:bg-n-ruby-4 transition-colors"
                      @click="removeManager(manager)"
                    >
                      Remover
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="py-6 text-center text-n-slate-11">
            Nenhum gestor cadastrado. Adicione gestores para receber
            notificações de leads.
          </div>
        </div>

        <!-- Send Digest Now -->
        <div class="p-4 rounded-lg border bg-n-solid-2 border-n-weak">
          <h3 class="mb-3 text-sm font-semibold text-n-slate-12">
            Enviar resumo agora
          </h3>
          <p class="mb-3 text-xs text-n-slate-10">
            Envia um resumo dos leads das últimas 24 horas para todos os
            gestores ativos.
          </p>
          <button
            class="flex gap-2 items-center py-1.5 px-4 text-sm font-medium text-white rounded-lg transition-colors"
            :class="
              isSendingDigest ||
              !managers.filter(m => m.active).length ||
              !selectedManagerInbox
                ? 'bg-n-slate-8 cursor-not-allowed'
                : 'bg-n-blue-9 hover:bg-n-blue-10'
            "
            :disabled="
              isSendingDigest ||
              !managers.filter(m => m.active).length ||
              !selectedManagerInbox
            "
            @click="sendDigestNow"
          >
            <Spinner v-if="isSendingDigest" size="tiny" />
            <span>Enviar resumo para gestores</span>
          </button>
          <p v-if="!selectedManagerInbox" class="mt-2 text-xs text-n-amber-11">
            Selecione uma caixa de entrada nas configurações acima.
          </p>

          <div
            v-if="digestResult && !digestResult.error"
            class="p-3 mt-3 rounded-lg bg-n-teal-2"
          >
            <p class="text-sm font-medium text-n-teal-11">
              Resumo enviado: {{ digestResult.sent }} gestores notificados ({{
                digestResult.total_leads
              }}
              leads)
            </p>
          </div>
          <div
            v-if="digestResult && digestResult.error"
            class="p-3 mt-3 rounded-lg bg-n-ruby-2"
          >
            <p class="text-sm font-medium text-n-ruby-11">
              Erro ao enviar resumo. Verifique as configurações.
            </p>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
